import React, {forwardRef} from 'react';
import {noop} from 'lodash';

import {VStack} from '../Stack';
import {type MenuGroupProps} from './_types';
import {useSelectionMode} from '../../hooks';

export const MenuGroupContext = React.createContext<{
  getIsSelected: (value: string) => boolean;
  labelId: string;
  selectionMode: 'none' | 'single' | 'multiple';
  shouldHideSelectedCheck?: boolean;
  toggleSelectedValue: (value: string) => void;
}>({
  getIsSelected: () => false,
  labelId: '',
  selectionMode: 'none',
  shouldHideSelectedCheck: false,
  toggleSelectedValue: noop,
});

export const useMenuGroupContext = () => React.useContext(MenuGroupContext);

export const MenuGroup = forwardRef<HTMLDivElement, MenuGroupProps>(
  (
    {
      children,
      onChange,
      selectionMode = 'none',
      shouldHideSelectedCheck,
      value,
      ...props
    }: MenuGroupProps,
    ref
  ) => {
    const labelId = React.useId();

    const {toggleSelectedValue, getIsSelected} =
      // @ts-expect-error - For some reason, typescript can't pass discriminated union types through to discriminated union types. ¯\_(ツ)_/¯
      useSelectionMode({
        onChange,
        selectionMode,
        value,
      });

    return (
      <MenuGroupContext.Provider
        value={{
          getIsSelected,
          labelId,
          selectionMode,
          shouldHideSelectedCheck,
          toggleSelectedValue,
        }}
      >
        <VStack
          aria-labelledby={labelId}
          data-sc="MenuGroup"
          ref={ref}
          role="group"
          spacing={0}
          {...props}
        >
          {children}
        </VStack>
      </MenuGroupContext.Provider>
    );
  }
);
